/**
 * Session bootstrap hook.
 *
 * Runs `refresh()` then `me()` exactly once per page load to discover
 * whether the user has a valid HttpOnly refresh cookie. Outcomes:
 *
 *   pending — work is still in flight; route layer renders a full-page
 *             loader so protected routes never paint with a stale or
 *             absent session.
 *   ready   — refresh succeeded, /me succeeded; access token is in
 *             memory and `user` is populated. Routes render normally.
 *   guest   — any failure along the way; cache + auth state are
 *             cleared via `clearSessionAndCache`. Routes render the
 *             guest tree.
 *
 * StrictMode safety:
 *   We split "start the work once" from "subscribe to the result".
 *   The work is a *module-level singleton promise* (`bootstrapPromise`)
 *   that starts on the first call to `ensureBootstrap` and is then
 *   shared by every later subscriber. Each `useEffect` mount attaches
 *   a fresh `.then()` callback guarded by a per-effect `cancelled`
 *   flag, so:
 *
 *     1. First effect mounts, attaches subscriber A, returns a cleanup
 *        that flips A's `cancelled` to true.
 *     2. StrictMode immediately runs cleanup; A is muted.
 *     3. Second effect mounts, attaches subscriber B with its own
 *        `cancelled = false`.
 *     4. Singleton promise resolves; A is muted (skip) but B sets
 *        `status` correctly.
 *
 *   The previous `startedRef` design combined the two concerns and the
 *   second mount returned early, leaving status stuck on 'pending'
 *   forever in dev.
 */

import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { refreshAccessToken } from '@/api/refresh-client';
import { authService } from '@/features/auth/api/auth.service';
import { useAuthStore } from '@/state/stores/auth.store';
import { clearSessionAndCache } from '@/core/auth/clear-session';
import { beginNotificationSession } from '@/core/notifications';
import { resetSessionRecovery } from '@/core/auth/session-recovery';

export type SessionBootstrapStatus = 'pending' | 'ready' | 'guest';

let bootstrapPromise: Promise<SessionBootstrapStatus> | null = null;

function ensureBootstrap(queryClient: QueryClient): Promise<SessionBootstrapStatus> {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async (): Promise<SessionBootstrapStatus> => {
    try {
      const { accessToken } = await refreshAccessToken();
      useAuthStore.getState().setAccessToken(accessToken);

      const user = await authService.me();
      useAuthStore.getState().setUser(user);
      resetSessionRecovery();
      beginNotificationSession();

      return 'ready';
    } catch {
      clearSessionAndCache({ queryClient });
      return 'guest';
    }
  })();

  return bootstrapPromise;
}

/**
 * Test-only seam — discards the cached bootstrap promise so the next
 * `useSessionBootstrap` call kicks off a fresh round trip. Production
 * code MUST NOT call this; the singleton is intentional.
 */
export function __resetSessionBootstrapForTests(): void {
  bootstrapPromise = null;
}

export function useSessionBootstrap(): SessionBootstrapStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionBootstrapStatus>('pending');

  useEffect(() => {
    let cancelled = false;

    ensureBootstrap(queryClient).then((resolved) => {
      if (!cancelled) setStatus(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  return status;
}
